import { CloseNotificationButton } from '@openx/components-core/lib/Snackbar/CloseNotificationButton';
import { usePackageContext } from '@openx/components-package/utils/context/PackageContext';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext';
import type { OptionsMap } from '@openx/types/options';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealContext } from '../../utils/context/DealContext';

const EMPTY_OBJECT = {};

export type UseTargetablePckgsProps = {
  editedDealId?: string;
  pckgUid?: string | null;
  onPckgNotAllowed: () => void;
};

export type UseTargetablePckgsReturn = {
  onAccountChange: (accountUid: string) => void;
  targetablePckgsAreLoading: boolean;
  targetablePckgs: OptionsMap;
  targetableCuratedPckgs: OptionsMap;
};

export type UseTargetablePckgsFn = (props: UseTargetablePckgsProps) => UseTargetablePckgsReturn;

export const useTargetablePckgs: UseTargetablePckgsFn = ({ editedDealId, onPckgNotAllowed, pckgUid }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { useTargetablePackagesFetch } = usePackageContext();
  const { useCurrentInstanceState } = useTargetingContext();
  const { isBefInstance } = useCurrentInstanceState();
  const { isOa } = useDealContext();

  const isPublisher = !isOa && !isBefInstance;

  const initialAccountUid = useRef<string>(undefined);
  const [accountUid, setAccountUid] = useState<string | null>(null);
  const [isFirstFetchDone, setIsFirstFetchDone] = useState<boolean>(false);

  useEffect(() => {
    if (!initialAccountUid.current && accountUid) {
      initialAccountUid.current = accountUid;
    }
  }, [accountUid]);

  const {
    arePackagesFetching: targetablePckgsIsFetching,
    arePackagesLoading: targetablePckgsIsLoading,
    packages: targetablePckgs,
  } = useTargetablePackagesFetch({
    accountUid,
    dealUid: editedDealId,
    noCache: true,
  });

  const {
    arePackagesFetching: targetableCuratedPckgsIsFetching,
    arePackagesLoading: targetableCuratedPckgsIsLoading,
    packages: targetableCuratedPckgs,
  } = useTargetablePackagesFetch({
    /** Disable Targetable Curated Packages fetch for Publisher instances */
    accountUid: isPublisher ? null : accountUid,
    dealUid: editedDealId,
    noCache: true,
    curatedOnly: true,
  });

  const targetablePckgsAreLoading =
    targetablePckgsIsLoading ||
    targetablePckgsIsFetching ||
    targetableCuratedPckgsIsLoading ||
    targetableCuratedPckgsIsFetching;

  useEffect(() => {
    if (isFirstFetchDone) {
      return;
    }

    if (!targetablePckgsAreLoading) {
      setIsFirstFetchDone(true);
    }
  }, [isFirstFetchDone, targetablePckgsAreLoading]);

  const showNotification = useCallback(() => {
    const notificationText =
      initialAccountUid.current === accountUid
        ? t(
            'Selected package cannot be used as it cannot be cloned. Please create a new package or select another one.'
          )
        : t(
            "Selected package cannot be used as it's not available under selected account/organization. Please create a new package or select another one."
          );

    enqueueSnackbar(notificationText, {
      action: key => <CloseNotificationButton action={() => closeSnackbar(key)} />,
      variant: 'error',
    });
  }, [enqueueSnackbar, t, closeSnackbar, accountUid]);

  useEffect(() => {
    if (targetablePckgsAreLoading || !pckgUid || !accountUid || !isFirstFetchDone) {
      return;
    }

    if (targetablePckgs?.[pckgUid] || targetableCuratedPckgs?.[pckgUid]) {
      return;
    }

    showNotification();
    onPckgNotAllowed();
  }, [
    pckgUid,
    accountUid,
    isFirstFetchDone,
    targetablePckgsAreLoading,
    targetablePckgs,
    targetableCuratedPckgs,
    showNotification,
    onPckgNotAllowed,
  ]);

  return {
    onAccountChange: setAccountUid,
    targetablePckgsAreLoading,
    targetablePckgs: targetablePckgs || EMPTY_OBJECT,
    targetableCuratedPckgs: targetableCuratedPckgs || EMPTY_OBJECT,
  };
};
